/**
 * numberFilters.js — advanced client-side filters for premium mobile numbers.
 * Works on either `item.number` (raw) or `item.displayNumber` (already-masked
 * with letters like X/Y/Z). We match on digits only.
 */

const toDigits = (v) => String(v || '').replace(/\D/g, '');

/** Number starts with the given digits (ignoring leading zeros / dashes). */
export function startsWith(item, prefix) {
  if (!prefix) return true;
  const raw = toDigits(item.number || item.displayNumber);
  return raw.startsWith(toDigits(prefix));
}

/** Number ends with the given digits. */
export function endsWith(item, suffix) {
  if (!suffix) return true;
  const raw = toDigits(item.number || item.displayNumber);
  return raw.endsWith(toDigits(suffix));
}

/** Number contains ALL given tokens (space or comma separated). */
export function containsAll(item, tokens) {
  if (!tokens) return true;
  const raw = toDigits(item.number || item.displayNumber);
  const parts = String(tokens)
    .split(/[\s,،]+/)
    .map((t) => toDigits(t))
    .filter(Boolean);
  if (parts.length === 0) return true;
  return parts.every((p) => raw.includes(p));
}

/** Number contains ANY of the given tokens. */
export function containsAny(item, tokens) {
  if (!tokens) return true;
  const raw = toDigits(item.number || item.displayNumber);
  const parts = String(tokens)
    .split(/[\s,،]+/)
    .map((t) => toDigits(t))
    .filter(Boolean);
  if (parts.length === 0) return true;
  return parts.some((p) => raw.includes(p));
}

/** Detect visual patterns on the last 4-6 digits. */
export function hasPattern(item, pattern) {
  if (!pattern || pattern === 'any') return true;
  const raw = toDigits(item.number || item.displayNumber);
  if (raw.length < 4) return false;

  // Look at the last 4-6 significant digits
  const tail4 = raw.slice(-4);
  const tail6 = raw.slice(-6);
  const [a, b, c, d] = tail4;

  switch (pattern) {
    // 4 identical digits (e.g. 7777)
    case 'quad':
      return a === b && b === c && c === d;
    // 3 identical digits in a row
    case 'triple':
      return /(\d)\1{2}/.test(raw);
    // Two pairs — AABB
    case 'aabb':
      return a === b && c === d && a !== c;
    // Alternating — ABAB
    case 'abab':
      return a === c && b === d && a !== b;
    // Palindrome ABBA
    case 'abba':
      return a === d && b === c && a !== b;
    // XYZXYZ (last 6 digits repeat as two triplets)
    case 'xyzxyz':
      return tail6.length === 6 && tail6.slice(0, 3) === tail6.slice(3);
    // Ascending consecutive (e.g. 1234, 6789)
    case 'ascending':
      return (
        parseInt(b, 10) === parseInt(a, 10) + 1 &&
        parseInt(c, 10) === parseInt(b, 10) + 1 &&
        parseInt(d, 10) === parseInt(c, 10) + 1
      );
    // Descending consecutive
    case 'descending':
      return (
        parseInt(b, 10) === parseInt(a, 10) - 1 &&
        parseInt(c, 10) === parseInt(b, 10) - 1 &&
        parseInt(d, 10) === parseInt(c, 10) - 1
      );
    // Mirror (first half = reversed second half)
    case 'mirror': {
      const t = tail6.length === 6 ? tail6 : tail4;
      const half = Math.floor(t.length / 2);
      return t.slice(0, half) === t.slice(-half).split('').reverse().join('');
    }
    default:
      return true;
  }
}

export const PATTERNS_AR = [
  { value: 'any',       label: 'كل الأنماط' },
  { value: 'quad',      label: 'رباعي متطابق (7777)' },
  { value: 'triple',    label: 'ثلاثي متطابق (777)' },
  { value: 'aabb',      label: 'زوجي (AABB)' },
  { value: 'abab',      label: 'متناوب (ABAB)' },
  { value: 'abba',      label: 'مرآة (ABBA)' },
  { value: 'xyzxyz',    label: 'ثلاثي مكرر (XYZXYZ)' },
  { value: 'ascending', label: 'تصاعدي (1234)' },
  { value: 'descending',label: 'تنازلي (9876)' },
];

export const PATTERNS_EN = [
  { value: 'any',        label: 'All patterns' },
  { value: 'quad',       label: 'Quad repeat (7777)' },
  { value: 'triple',     label: 'Triple repeat (777)' },
  { value: 'aabb',       label: 'Pairs (AABB)' },
  { value: 'abab',       label: 'Alternating (ABAB)' },
  { value: 'abba',       label: 'Mirror (ABBA)' },
  { value: 'xyzxyz',     label: 'Repeating trio (XYZXYZ)' },
  { value: 'ascending',  label: 'Ascending (1234)' },
  { value: 'descending', label: 'Descending (9876)' },
];
